import { useEffect } from 'react';
// import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import PublicSessions from './pages/PublicSessions';
import PrivateSessions from './pages/PrivateSessions';
import Tutors from './pages/Tutors';
import TutorAddEdit from './components/Tutor/TutorAddEdit';
import TutorView from './components/Tutor/TutorView';
import TopicView from './components/Topic/TopicView';
import Topics from './pages/Topics';
import SessionAdd from './pages/SessionAdd';
import PublicMultipleSessions from './pages/PublicMultipleSessions';
// import Playground from './pages/Playground';
import Analytics from './pages/Analytics';
import Block from './pages/Block';
import { UserProvider, useUserContext } from './components/Providers/UserContext';
import { TimeZoneProvider } from './providers/TimeZoneProvider';
import { TutorProvider } from './providers/TutorContext';
import NavHeader from './components/layout/NavHeader';
import { SessionProvider } from 'providers/SessionContext';
import TopicAdd from './pages/TopicAdd';
import { TopicProvider } from 'providers/TopicContext';
import { PrivateGroupsProvider } from './providers/privateGroupsContext';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useFetchTopics from './Query/useFetchTopics';
import useFetchTutors from './Query/useFetchTutors';
import Welcome from './pages/Welcome';
import Unauthorized from './pages/Unauthorized';
import { ApiError } from './utils/Network';

const AppBody = () => {
  // const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userToken, user, hasRole, init, isLoggedIn } = useUserContext();

  const {
    data: allTopics = [],
    error: TopicsError,
    isPending: PendingTopics,
  } = useFetchTopics(userToken);

  if (TopicsError) {
    console.log('An error has occurred: ' + TopicsError.message);
  }

  const {
    data: tutorList = [],
    error: TutorsError,
    isPending: PendingTutors,
  } = useFetchTutors(userToken);

  if (TutorsError) {
    console.log('An error has occurred: ' + TutorsError.message);
  }

  useEffect(() => {
    if (!hasRole(['ADMIN', 'SUPERVISOR', 'SUPER_USER', 'TEACHER']) && isLoggedIn) {
      navigate('/error', { replace: true });
    }
  }, [navigate, hasRole, isLoggedIn]);

  if (!init) return null;

  return (
    <Routes>
      <Route path="/login" element={<Welcome />} />
      <Route path="/error" element={<Unauthorized />} />
      {user ? (
        <>
          <Route
            path="/"
            element={
              <div className="page-container">
                <NavHeader />
                <PublicSessions />
              </div>
            }
          />
          <Route
            path="/publicsessions"
            element={
              <div className="page-container">
                <NavHeader />
                <PublicSessions />
              </div>
            }
          />
          <Route
            path="/sessionadd"
            element={
              <div className="page-container">
                <NavHeader />
                <SessionAdd tutorList={tutorList} topicList={allTopics} />
              </div>
            }
          />
          <Route
            path="/multiplesessionaddpublic"
            element={
              <div className="page-container">
                <NavHeader />
                <PublicMultipleSessions />
              </div>
            }
          />
          <Route
            path="/privatesessions/:groupId/*"
            element={
              <div className="page-container">
                <NavHeader />
                <PrivateSessions tutorList={tutorList} topicList={allTopics} />
              </div>
            }
          />
          <Route
            path="/tutor"
            element={
              <div className="page-container">
                <NavHeader />
                <Tutors tutorList={tutorList} isLoading={PendingTutors} />
              </div>
            }
          />
          <Route
            path="/tutoradd"
            element={
              <div className="page-container">
                <NavHeader />
                <TutorAddEdit />
              </div>
            }
          />
          <Route
            path="/tutorview"
            element={
              <div className="page-container">
                <NavHeader />
                <TutorView />
              </div>
            }
          />
          <Route
            path="/topics"
            element={
              <div className="page-container">
                <NavHeader />
                <Topics topicList={allTopics} isLoading={PendingTopics} />
              </div>
            }
          />
          <Route
            path="/topicview/:topicId"
            element={
              <div className="page-container">
                <NavHeader />
                <TopicView />
              </div>
            }
          />
          <Route
            path="/topicadd"
            element={
              <div className="page-container">
                <NavHeader />
                <TopicAdd />
              </div>
            }
          />
          <Route
            path="/block"
            element={
              <div className="page-container">
                <NavHeader />
                <Block />
              </div>
            }
          />
          <Route
            path="/analytics"
            element={
              <div className="page-container">
                <NavHeader />
                <Analytics />
              </div>
            }
          />
        </>
      ) : (
        <Route path="*" element={<Navigate replace to="/login" />} />
      )}
    </Routes>
  );
};

function App() {
  const navigate = useNavigate();

  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error: Error) => {
        if (error instanceof ApiError && error?.httpStatus === 401) {
          console.info('Session expired, redirecting to login...');
          localStorage.removeItem('session_token');
          localStorage.removeItem('user_info');
          navigate('/login');
        }
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.httpStatus === 401) {
            return false;
          }
          return failureCount > 3;
        },
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <TutorProvider>
          <SessionProvider>
            <TopicProvider>
              <TimeZoneProvider>
                <PrivateGroupsProvider>
                  <AppBody />
                </PrivateGroupsProvider>
              </TimeZoneProvider>
            </TopicProvider>
          </SessionProvider>
        </TutorProvider>
      </UserProvider>
    </QueryClientProvider>
  );
}

export default App;
